import { AccountSourceType } from 'owa-account-source-list-types';
import type TokenResponse from 'owa-service/lib/contract/TokenResponse';
import type TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import { MSA_DEFAULT_SCOPE, OUTLOOK_COBRAND_ID, PENDING_REDIRECT_KEY } from './utils/constants';
import { getMsalV5BridgeState } from 'owa-tokenprovider-utils/lib/getMsalV5BridgeState';
import { version as msalVersion } from '@azure/msal-browser-1p';
import { getMsalInstance, getClientId } from './initializeMsalLibrary';
import { msalAuthenticationResultToTokenResponse } from './utils/msalAuthenticationResultToTokenResponse';
import { type StringDict, createClientAuthError, createAuthError } from '@azure/msal-common';
import {
    CacheLookupPolicy,
    PromptValue,
    type AccountInfo,
    type AuthenticationResult,
    type IPublicClientApplication,
    type PopupRequest,
    type RedirectRequest,
    type SsoSilentRequest,
    type SilentRequest,
} from '@azure/msal-browser-1p';
import { getScopes } from './utils/getScopes';
import { InteractionType } from './utils/InteractionType';
import type { MailboxInfo } from 'owa-client-types';
import { getLoginHint } from './utils/getLoginHint';
import { getQueryStringParameters, stringify } from 'owa-querystring';
import { getLogAuthErrorData, getLogAuthResultData } from './utils/logger';
import { getMsalVersion } from './getMsalVersion';
import { logStartCoreUsage } from 'owa-analytics-start';
import { isUrlPresent, overrideSessionId } from 'owa-config';
import { getSilentRedirectUri } from './utils/getRedirectUri';
import { getGuid } from 'owa-guid';
import {
    setItem as sessionStorageSetItem,
    removeItem as sessionStorageRemoveItem,
} from 'owa-session-storage';
import { base64UrlEncode } from 'owa-tokenprovider-utils/lib/base64UrlEncode';
import { getAuthority, getCommonAuthorityUrlFromMetaTag } from './utils/getAuthority';
import {
    setActiveAccountRelatedProperties,
    setMailboxInfoToAccountMapping,
} from './utils/MsalAccounts';
import { getTypeHintFromAccountInfo } from './getTypeHint';
import { setLoginHintForAadOrMsaRedirect } from './utils/getAadOrMsaRedirectUri';
import { getHostLocation } from 'owa-url/lib/hostLocation';
import { deleteSidCookie } from './utils/clearCookies';
import { acquireTokenSilently } from './acquireAccessTokenMsal';
import { isBootFeatureEnabled } from 'owa-metatags';
import { getItem } from 'owa-local-storage';
import { isConsumerDomain } from './isMsalEnabledForConsumerDomain';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { validateIdTokenIdentity } from './utils/validateIdTokenIdentity';
import { preserveRedirectSid, captureIncomingPromptFromState } from './utils/marketingRedirectSid';
import { INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT } from 'owa-analytics-types';
import type { MsalLoginPopupScenario } from './MsalLoginPopupScenario';

const DISALLOWED_QUERY_PARAMS = ['login_hint', 'prompt', 'realm', 'domain_hint'];

/**
 * Login user to the app with MSAL.
 * @param typeHint account type hint (e.g., OutlookDotCom, Office365)
 * @param interactionType what interaction type should be used (e.g., Silent, Popup, Redirect)
 * @param msalAccount MSAL account that should be used for login
 * @param mailboxInfo if account is not provided, mailboxInfo will be used to look up available account in MSAL cache
 * @param username if neither msalAccount nor mailboxInfo is provided, username (email address) will be passed as the login hint if provided.
 * @param resource additional resource to request access to
 * @param scope additional scope to request access to
 * @param popupScenario identifies the user experience that initiated a popup login
 **/
export async function loginUserMsal(
    typeHint: AccountSourceType,
    interactionType: InteractionType,
    msalAccount?: AccountInfo,
    mailboxInfo?: MailboxInfo,
    username?: string,
    resource?: string,
    scope?: string,
    promptValue?: string,
    claims?: string,
    correlationId?: string,
    popupScenario?: MsalLoginPopupScenario
): Promise<TokenResponse> {
    let authResult: AuthenticationResult;

    try {
        correlationId = correlationId ?? getGuid();
        const msalInstance = await getMsalInstance();

        authResult = await loginUserMsalInternal(
            correlationId,
            msalInstance,
            interactionType,
            typeHint,
            msalAccount,
            mailboxInfo,
            username,
            resource,
            scope,
            promptValue,
            claims,
            popupScenario
        );

        if (authResult) {
            setActiveAccountRelatedProperties(msalInstance, authResult.account);
        }
    } catch (error) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS18046: 'error' is of type 'unknown'.
        // @ts-expect-error
        return { TokenResultCode: 2, SubErrorCode: error.errorCode };
    }

    return msalAuthenticationResultToTokenResponse(authResult, typeHint);
}

export async function loginUserMsalInternal(
    correlationId: string,
    msalInstance: IPublicClientApplication,
    interactionType: InteractionType,
    typeHint: AccountSourceType,
    msalAccount?: AccountInfo,
    mailboxInfo?: MailboxInfo,
    username?: string,
    resource?: string,
    scope?: string,
    promptValue?: string,
    claims?: string,
    popupScenario?: MsalLoginPopupScenario
): Promise<AuthenticationResult> {
    if (typeHint === AccountSourceType.Other) {
        throw createClientAuthError('UnsupportedAccountType', correlationId);
    }

    // Display Outlook Cobrand for MSAL Consumer accounts on redirect/popup requests
    const extraQueryParams: StringDict = {};

    if (
        typeHint === AccountSourceType.OutlookDotCom &&
        (interactionType === InteractionType.Redirect || interactionType === InteractionType.Popup)
    ) {
        // Add extra query params to add Outlook cobranding, block EASI ID creation
        // fl = dob, flname, wld creates an @outlook.com account and forces collection of DOB, country, and first and last name
        // Note: The EASI ID creation needs to be blocked for both Sign In and Sign Up scenarios
        extraQueryParams.cobrandid = OUTLOOK_COBRAND_ID;
        extraQueryParams.fl = 'dob,flname,wld';

        // Add extra query params to show create account dialog
        // https://learn.microsoft.com/en-us/entra/identity-platform/reference-msa-server-side-api
        if (promptValue === PromptValue.CREATE) {
            extraQueryParams.signup = '1';
        }
    }

    const originalUrl = `${getOrigin() + getRequestUrlWithoutDisallowedParams()}`;
    const encodedUrl = base64UrlEncode(originalUrl);
    const msalState = getMsalV5BridgeState(msalVersion);
    const stateParam = msalState + '|' + encodedUrl;

    const request: SsoSilentRequest &
        PopupRequest &
        RedirectRequest &
        Pick<SilentRequest, 'cacheLookupPolicy'> = {
        scopes: getScopes(typeHint, resource, scope, interactionType === InteractionType.Redirect),
        account: msalAccount ?? undefined,
        loginHint: getLoginHint(msalAccount, mailboxInfo, username),
        state: stateParam,
        authority: getAuthority(typeHint),
        prompt: promptValue,
        extraQueryParameters: extraQueryParams,
        redirectUri: getSilentRedirectUri(),
        claims,
        correlationId,

        // msal-browser@3.26.1 has a regression in NAA where cacheLookupPolicy is not properly defaulted to
        // CacheLookupPolicy.Default. Explicitly pass Default here as a workaround until MSAL fix is in.
        cacheLookupPolicy: CacheLookupPolicy.Default,
    };

    const startTime = self.performance.now();
    const isAccountPresent = !!msalAccount;
    let authResult: AuthenticationResult;

    try {
        if (interactionType === InteractionType.Redirect) {
            // Keep the encoded URL as the first state segment; when a prompt is
            // present, append it as a base64url-JSON marker (same encoding as the
            // silent/popup msalVersion marker) so it survives the IdP round-trip
            // for clientstartup telemetry.
            const redirectState = promptValue
                ? encodedUrl + '|' + base64UrlEncode(JSON.stringify({ prompt: promptValue }))
                : encodedUrl;
            await loginRedirect(msalInstance, { ...request, state: redirectState }, typeHint);
            throw createAuthError('NotRedirected', correlationId); // make TS happy
        } else if (interactionType === InteractionType.Popup) {
            authResult = await msalInstance.loginPopup(request);
        } else {
            authResult = await msalInstance.ssoSilent(request);
        }

        // Observe-only: log mismatches between the identity in the token and the expected account
        if (isBootFeatureEnabled('auth-login-hint-validate-login') && mailboxInfo) {
            validateIdTokenIdentity(authResult, mailboxInfo, request.loginHint);
        }
    } catch (error) {
        logStartCoreUsage(
            'MsalLogin',
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
             *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
            {
                ...getLogAuthErrorData(
                    interactionType,
                    // Errors resulting from enabling useUnknownInCatchVariables
                    // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'AuthError'.
                    // @ts-expect-error
                    error,
                    startTime,
                    'LoginUserMsal',
                    request.scopes,
                    typeHint,
                    !!request.authority && isUrlPresent(request.authority),
                    undefined /*isCAERemediationRequired*/,
                    undefined /*isPageVisible*/,
                    undefined /*isSigninRequired*/,
                    isAccountPresent
                ),
                ...(popupScenario ? { popupScenario } : {}),
            }
        );

        throw error;
    }

    logStartCoreUsage(
        'MsalLogin',
        /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
         *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
        INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
        {
            ...getLogAuthResultData(
                interactionType,
                authResult,
                startTime,
                typeHint,
                !!request.authority && isUrlPresent(request.authority),
                undefined /*isPageVisible*/,
                undefined /*requestedScope*/,
                isAccountPresent
            ),
            ...(popupScenario ? { popupScenario } : {}),
        }
    );

    if (mailboxInfo) {
        setMailboxInfoToAccountMapping(mailboxInfo, authResult.account);
    }

    return authResult;
}

function loginRedirect(
    msalInstance: IPublicClientApplication,
    request: RedirectRequest,
    typeHint: AccountSourceType
): Promise<void> {
    const queryParams = getQueryStringParameters();
    const realmQueryParam = queryParams['realm'] ?? queryParams['domain_hint'];
    const domainHint = getItem(self, 'domain_hint');

    // Making sure the client doesn't pass the realm query param to MSAL if it's an account switching scenario or consumer domain
    if (
        isBootFeatureEnabled('auth-msaljs-domainHint') &&
        !isConsumerDomain() &&
        request.prompt != PromptValue.SELECT_ACCOUNT
    ) {
        if (realmQueryParam && (!request.loginHint || request.loginHint.indexOf('@') > -1)) {
            // Only when loginHint is missing or loginHint is an email address.
            request.domainHint = realmQueryParam;
        } else if (!request.loginHint && domainHint) {
            // When realm is missing, but domainHint is available in cache. Pass it to MSAL if loginHint is not provided
            request.domainHint = domainHint;
        }
    }

    if (isConsumerDomain() && request.prompt == PromptValue.CREATE) {
        request.loginHint = undefined; // Don't pass loginHint for create account flow
    }

    // Initiate the login process.
    // Use the /common endpoint for fresh login attempts or account switching to support cross-domain login and show both business and consumer accounts.
    // Prompt and authority should not be overriden when domainHint is set already.
    if (
        !request.domainHint &&
        (!request.loginHint || request.prompt == PromptValue.SELECT_ACCOUNT)
    ) {
        request.prompt = request.prompt ?? PromptValue.SELECT_ACCOUNT; // Default to SELECT_ACCOUNT and skip SSO attempt when loginHint is not provided or if it's an account switching scenario

        // Create account flow must use the /consumers endpoint. Otherwise, use /common
        if (request.prompt != PromptValue.CREATE) {
            request.authority = getCommonAuthorityUrlFromMetaTag();
        }
    }

    request.redirectUri = undefined; // use the redirectUri from the MSAL config
    request.redirectStartPage = getRequestUrlWithoutDisallowedParams();

    // Log the start of the login process, before the page redirect.

    /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
     * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
     *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
    logStartCoreUsage('MsalLogin', INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT, {
        accountType: typeHint,
        interactionType: InteractionType.Redirect,
        prompt: request.prompt,
        isValidAuthorityUrl: isUrlPresent(request.authority ?? ''),
        correlationId: request.correlationId,
        applicationId: getClientId(),
        msalVersion: getMsalVersion(),
        loginHint: request.loginHint
            ? request.loginHint.indexOf('@') > -1
                ? 'UPN'
                : 'Opaque String'
            : 'None',
    });

    // set a temporary flag in session storage to indicate the page load should
    // use MSAL flow to handle the incoming redirect
    sessionStorageSetItem(self, PENDING_REDIRECT_KEY, '1');

    return msalInstance.loginRedirect(request);
}

export async function handleLoginRedirectPromise(
    msalInstance: IPublicClientApplication,
    domainTypeHint: AccountSourceType,
    correlationId: string,
    claims: string | undefined
): Promise<AuthenticationResult | null> {
    const startTime = self.performance.now();

    try {
        sessionStorageRemoveItem(self, PENDING_REDIRECT_KEY);

        captureIncomingPromptFromState(
            new URLSearchParams(self.owaLocationHash?.replace(/^#/, '')).get('state')
        );
        let authResult = await (msalInstance.handleRedirectPromise as Function)({
            hash: self.owaLocationHash,
            navigateToLoginRequestUrl: true,
        });

        if (authResult) {
            captureAndStripRedirectSid();

            // Remove the sid cookie
            deleteSidCookie();

            // If the account is different from the domain type hint, redirect to the correct domain.
            const accountInfoTypeHint = getTypeHintFromAccountInfo(authResult.account);
            if (
                domainTypeHint != AccountSourceType.Other &&
                domainTypeHint != accountInfoTypeHint
            ) {
                setLoginHintForAadOrMsaRedirect(getLoginHint(authResult.account));
                throw new Error('AadOrMsaRedirRequired');
            }

            const isMSAFullTrustTokenRequired =
                !isBootFeatureEnabled('auth-msaljs-useDelegationToken') &&
                getTypeHintFromAccountInfo(authResult.account) === AccountSourceType.OutlookDotCom;
            if (isMSAFullTrustTokenRequired) {
                authResult = await acquireTokenSilently(
                    correlationId,
                    msalInstance,
                    authResult.account,
                    domainTypeHint,
                    'Unknown apiName',
                    undefined, // resource
                    MSA_DEFAULT_SCOPE, // scope
                    claims
                );
            }

            // Coming back from a successful authentication redirect and token fetching.

            logStartCoreUsage(
                'MsalLoginRedirectPromise',
                /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
                 * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
                 *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
                INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
                getLogAuthResultData(
                    InteractionType.Redirect,
                    authResult,
                    startTime,
                    domainTypeHint,
                    isUrlPresent(authResult.authority)
                )
            );

            return authResult;
        }
    } catch (error) {
        logStartCoreUsage(
            'MsalLoginRedirectPromise',
            /* eslint-disable-next-line owa-custom-rules/telemetry-no-global-account-routing -- (https://aka.ms/OWALintWiki)
             * Baseline. DO NOT COPY AND PASTE! - This is a system metadata event
             *	> Only internal analytics system metadata events should use the global account key. All other events should use a valid Mailbox. https://aka.ms/owa-analytics-mailboxinfo */
            INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT,
            getLogAuthErrorData(
                InteractionType.Redirect,
                // Errors resulting from enabling useUnknownInCatchVariables
                // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'AuthError'.
                // @ts-expect-error
                error,
                startTime,
                'Unknown apiName',
                undefined /*scopes*/,
                domainTypeHint
            )
        );
        throw error;
    }

    // No redirect was detected
    return null;
}

export function captureAndStripRedirectSid(): void {
    const queryParams = getQueryStringParameters(self.location);
    const { sessionId } = queryParams;
    preserveRedirectSid(sessionId);

    if (!sessionId) {
        return;
    }

    // Seed sessionId before stripping it from the URL so getSessionId() keeps
    // returning the marketing-page sid instead of generating a new guid.
    overrideSessionId(sessionId);

    delete queryParams.sessionId;
    const newQuery = stringify(queryParams);
    const url = `${getOrigin()}${self.location.pathname}${newQuery ? '?' + newQuery : ''}${
        self.location.hash
    }`;
    self.history.replaceState(null, '', url);
}

function getRequestUrlWithoutDisallowedParams(): string {
    // delete disallowed query params to prevent redirect loop
    const queryStringParams = getQueryStringParameters();
    const allowedParams = Object.fromEntries(
        Object.entries(queryStringParams).filter(([key]) => !DISALLOWED_QUERY_PARAMS.includes(key))
    );

    const path = getHostLocation().pathname;
    if (Object.keys(allowedParams).length > 0) {
        return path + '?' + stringify(allowedParams);
    }

    return path;
}
